import * as React from "react";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { KeyValue, Query, ResultRecord, SortDirection } from "../../interfaces";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import BackButton from "../../containers/BackButton";
import LeavingFDICDialog from "../LeavingFDICDialog";
import { ApiBaseUrl, AppUrl } from "../../config";
import { apiPost, fetchData } from "../../common/services/ApiClient";
import { getReportPeriod } from "./FinancialReportingUtilities";
import FinancialReportDetailsSearch from "../../containers/FinancialReportDetailsSearchForm";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { setInstitutionStatus } from "../../common/services/Utilities";
import ActiveStatus from "../ActiveStatus";
import clsx from "clsx";
import { AppBody } from "../AppBody";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

const PREFIX = "FinancialReportingDetails";

const classes = {
  // root: `${PREFIX}-root`,
  subHeader: `${PREFIX}-subHeader`,
  linkButtons: `${PREFIX}-linkButtons`,
  leaveFDICDialog: `${PREFIX}-leaveFDICDialog`,
  inactiveButtons: `${PREFIX}-inactiveButtons`,
  FRNoData: `${PREFIX}-FRNoData`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.subHeader}`]: {
    "display": "flex",
    "position": "relative",
    "& > p ": {
      color: theme.palette.primary.main,
      position: "absolute",
      right: 0,
    },
  },

  [`& .${classes.linkButtons}`]: {
    minWidth: 175,
    height: 34,
    margin: "0px 16px 10px 0px",
    fontWeight: 500,
    fontFamily: "'Source Sans Pro', sans-serif",
  },

  [`& .${classes.leaveFDICDialog}`]: {
    "minWidth": 175,
    "marginRight": 16,
    "& .MuiTypography-root": {
      overflow: "inherit",
    },
    "& .MuiTypography-body1": {
      lineHeight: "2rem",
      fontWeight: 500,
    },
    "& a": {
      "border": "1px solid rgba(26, 68, 128, 0.5)",
      "borderRadius": 4,
      "display": "block",
      "minWidth": 175,
      "textAlign": "center",
      "color": theme.palette.primary.main,
      "textDecoration": "none",
      "&:visited": {
        color: theme.palette.primary.main,
      },
      "&:hover, &:focus": {
        border: `1px solid ${theme.palette.primary.main}`,
        textDecoration: "none",
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.secondary.main,
      },
    },
  },

  [`& .${classes.inactiveButtons}`]: {
    "display": "flex",
    "margin": "0px auto 10px 24px",
    "& > div": {
      margin: "0px auto",
    },
  },

  [`& .${classes.FRNoData}`]: {
    color: "#b3b3b3",
    backgroundColor: "#ececec",
    border: "1px solid #abc8df",
  },
}));

interface Props {
  appState: Query;
  updateReportPeriod: (reportPeriod: string[]) => void;
  updateLatestReportPeriod: (reportPeriod: string) => void;
  updatePagination: () => void;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
}
export const getCurrentReportFields = (fields: KeyValue[], array: any[]) => {
  fields.forEach((fieldVal: KeyValue) => {
    const ytdVal = fieldVal.variable_ytd;
    const ytdValr = fieldVal.variable_ytdr;
    const qtlyVal = fieldVal.variable_qtly;
    const qtlyValr = fieldVal.variable_qtlyr;

    if (ytdVal) {
      array.push(ytdVal);
    }
    if (ytdValr && ytdVal !== ytdValr) {
      array.push(ytdValr);
    }
    if (qtlyVal && ytdVal !== qtlyVal) {
      array.push(qtlyVal);
    }
    if (qtlyValr && ytdValr !== qtlyValr) {
      array.push(qtlyValr);
    }
    if (fieldVal.sub_fields) {
      getCurrentReportFields(fieldVal.sub_fields, array);
    }
  });
  return array;
};

export default function FinancialReportingDetails({
  appState,
  updateReportPeriod,
  updateLatestReportPeriod,
  updatePagination,
  updateSort,
}: Props) {
  // prevents these values being added to the query in the URL since they do nothing for the page.
  const [initialLoad, setInitialLoad] = React.useState(true);
  React.useEffect(() => {
    if (initialLoad) {
      updatePagination();
      updateSort("sortField", "");
      updateSort("sortOrder", "");
      setInitialLoad(false);
    }
  }, [updateSort, updatePagination, initialLoad]);
  let { cert } = useParams();
  cert = cert ? cert.split("?")[0] : 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [instName, setInstName] = React.useState("");
  const [timeStamp, setTimeStamp] = React.useState<string>("");
  const [risdate, setRisdate] = React.useState("");

  const [financialDetailsData, setFinancialDetailsData] = React.useState<
    KeyValue[]
  >([{}]);
  const [FRDetails, setFRDetails] = React.useState<ResultRecord>();
  const reportPeriod = appState.filters.reportPeriod;
  const isActive = !appState.ui.limitInactiveDate;

  // for inactive institutions, we want to reset the reportPeriod to the latest allowed date
  React.useEffect(() => {
    let isMounted = true;
    if (
      !isActive &&
      appState.filters.reportPeriod[0] > appState.ui.latestReportPeriod
    ) {
      if (isMounted) {
        updateReportPeriod([appState.ui.latestReportPeriod]);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [
    appState.filters.reportPeriod,
    appState.ui.latestReportPeriod,
    isActive,
    updateReportPeriod,
  ]);
  // Gets current institution name
  React.useEffect(() => {
    let isMounted = true;
    window.scrollTo(0, 0);
    (async () => {
      await fetchData(`${ApiBaseUrl()}/institutions?filters=CERT:${cert}`)
        .then((response) => {
          if (isMounted) {
            setInstName(response.data[0]?.NAME);
            setFRDetails(setInstitutionStatus(response.data[0]));
          }
        })
        .catch((error: Error) => {
          console.error(`${error.name} : ${error.message}`);
        });
    })();
    return () => {
      isMounted = false;
    };
  }, [cert]);

  // sets timestamp and risdate if null
  React.useEffect(() => {
    let isMounted = true;
    if (!timeStamp) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?limit=1&offset=0&filters=CERT:${cert}&fields=RISDATE&sort_by=RISDATE&sort_order=DESC`,
        )
          .then((response) => {
            if (isMounted) {
              const risDateMonth = response.data[0].RISDATE.substring(4, 6);
              const risDateDay = response.data[0].RISDATE.substring(6, 8);
              const risDateYear = response.data[0].RISDATE.substring(0, 4);
              setTimeStamp(`${risDateMonth}/${risDateDay}/${risDateYear}`);
              setRisdate(`${risDateMonth}${risDateDay}${risDateYear}`);
              if (!appState.filters.reportPeriod[0]) {
                updateReportPeriod([response.data[0].RISDATE]);
              }
              if (appState.ui.latestReportPeriod !== response.data[0].RISDATE) {
                updateLatestReportPeriod(response.data[0].RISDATE);
              }
            }
          })
          .catch(() => {
            // if theres an error, defaults to currentDate
            const currentDate = getReportPeriod(new Date());
            setTimeStamp(currentDate[2] + currentDate[0] + currentDate[1]);
            setRisdate(currentDate[2] + currentDate[0] + currentDate[1]);
            if (!appState.filters.reportPeriod[0]) {
              updateReportPeriod([currentDate]);
            }
            if (appState.ui.latestReportPeriod !== currentDate) {
              updateLatestReportPeriod(currentDate);
            }
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [
    appState.filters.reportPeriod,
    appState.ui.latestReportPeriod,
    cert,
    reportPeriod,
    timeStamp,
    updateLatestReportPeriod,
    updateReportPeriod,
  ]);

  const [previousReports, setPreviousReports] = React.useState([""]);
  const [previousFields, setPreviousFields] = React.useState([{}]);

  React.useEffect(() => {
    let isMounted = true;

    const filteredReports = reportPeriod.filter(
      (report: string) => report !== "",
    );
    const currentReports = filteredReports.map(
      (report: string, index: number) => `${appState.ui.reportType}_${report}`,
    );
    const isDemographic = appState.ui.reportType === "demographics";
    let fieldValues: any = [];

    if (
      appState.ui.reportStructure &&
      appState.ui.reportStructure[0]?.structureID &&
      appState.ui.reportType === appState.ui.reportStructure[0].structureID &&
      (currentReports.toString() !== previousReports.toString() ||
        (appState.ui.reportStructure[0].fields &&
          JSON.stringify(appState.ui.reportStructure[0].fields) !==
            JSON.stringify(previousFields)))
    ) {
      setPreviousReports(currentReports);
      setPreviousFields(appState.ui.reportStructure[0].fields);

      if (appState.ui.reportStructure[0].fields) {
        filteredReports.forEach(() => {
          fieldValues.push(
            `${getCurrentReportFields(
              appState.ui.reportStructure[0].fields,
              [],
            ).join()}`,
          );
        });
        if (
          filteredReports &&
          filteredReports.length === fieldValues.length &&
          (currentReports.toString() !== previousReports.toString() ||
            JSON.stringify(appState.ui.reportStructure[0].fields) !==
              JSON.stringify(previousFields))
        ) {
          const fetchFinancials = filteredReports.map(
            (report: string, index: number) => {
              return isDemographic
                ? [
                    fetchData(
                      `${ApiBaseUrl()}/financials`,
                      apiPost({
                        limit: 1,
                        offset: 0,
                        filters: `CERT:${cert} AND RISDATE:${report}`,
                        fields: `CERT,RISDATE,${fieldValues[index]}`,
                      }),
                    ),
                    fetchData(
                      `${ApiBaseUrl()}/cbis?filters=CERT:${cert} AND REPDTE:${report}`,
                    ),
                  ]
                : [
                    fetchData(
                      `${ApiBaseUrl()}/financials`,
                      apiPost({
                        limit: 1,
                        offset: 0,
                        filters: `CERT:${cert} AND RISDATE:${report}`,
                        fields: `CERT,RISDATE,${fieldValues[index]}`,
                      }),
                    ),
                  ];
            },
          );
          setPreviousReports(currentReports);
          (async () => {
            await Promise.all(
              [].concat
                .apply([], fetchFinancials)
                .map((apiUrl: string) => apiUrl),
            )
              .then((response) => {
                if (isMounted) {
                  const financialsData: KeyValue[] = [];
                  const cbisData: KeyValue[] = [];
                  response.forEach((res: any) =>
                    res.data[0]
                      ? financialsData.push(res.data[0])
                      : cbisData.push(res.data),
                  );
                  const combinedData = financialsData.map(
                    (val: KeyValue, int: number) => {
                      return { ...val, ...cbisData[int] };
                    },
                  );

                  setFinancialDetailsData(
                    isDemographic ? combinedData : financialsData,
                  );
                }
              })
              .catch((error: Error) => {
                console.error(`${error.name} : ${error.message}`);
              })
              .finally(() => (isMounted = false));
          })();
        }
      }
    }
  }, [
    appState.ui.incomeBasis,
    appState.ui.reportStructure,
    appState.ui.reportType,
    appState.ui.unitType,
    cert,
    previousFields,
    previousReports,
    reportPeriod,
  ]);

  useInitialPageLoad("financialsDetails");
  return (
    <Root>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle="Financial Reporting" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <Typography
        component="div"
        sx={{
          "& div#item_value_Institution_Closed_value": {
            lineHeight: "1rem",
          },
          "& div#item_value_active_status_sub_description_value": {
            lineHeight: "1rem",
          },
          "& form": {
            padding: "0",
          },
          "display": "inline-block",
          "& button#view-institution-details-button": {
            position: "static",
          },
          "& hr": {
            margin: "7px 0 12px 0",
          },
          "& a": {
            textTransform: "none",
          },
          "width": "100%",
        }}
        id="financialReporting-detail"
      >
        <AppBody
          login
          backButton={
            <BackButton
              app="FinancialReportSearch"
              buttonLabel="Back to Search Results"
              variant="dark"
            />
          }
          introText={
            <Typography
              id="inst-name-header"
              style={{
                paddingBottom: 5,
                marginBottom: 0,
                position: "absolute",
                bottom: 5,
              }}
              variant="h1"
            >
              {instName}
            </Typography>
          }
        >
          <>
            <Divider variant="fullWidth" />
            <Grid container>
              <Grid
                size={{
                  xs: 8,
                  sm: 10,
                }}
              >
                <Typography variant="h2">
                  Financial & Regulatory Reporting - FDIC Cert #{cert}
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 4,
                  sm: 2,
                }}
              >
                {!appState.ui.noFRdata && (
                  <Typography id="data-as-of-date">
                    Data as of {timeStamp}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Box
              sx={{
                mb: -5,
              }}
            >
              <Grid container>
                {!isActive && FRDetails && (
                  <Grid
                    size={{
                      xs: 12,
                      sm: 9,
                    }}
                  >
                    <ActiveStatus
                      institution={FRDetails}
                      levelTwo
                      appState={appState}
                    />
                  </Grid>
                )}
                <Grid
                  style={{
                    display: !isActive ? "block" : "inherit",
                  }}
                  size={{
                    sm: !isActive ? 3 : 12,
                  }}
                >
                  <Button
                    id="view-institution-details-button"
                    variant="outlined"
                    color="primary"
                    className={clsx(
                      classes.linkButtons,
                      !isActive && classes.inactiveButtons,
                    )}
                    onClick={() => {
                      navigate(`${AppUrl("BankFind")}/details/${cert}`, {
                        state: {
                          url: location.pathname + location.search,
                          // reportUrl: location.state?.reportUrl,
                        },
                      });
                    }}
                  >
                    View Institution Details
                  </Button>
                  {appState.ui.noFRdata ? (
                    <Button
                      disabled
                      className={clsx(classes.linkButtons, classes.FRNoData)}
                    >
                      Latest FFIEC Call Report
                    </Button>
                  ) : (
                    <Typography
                      id="latest-ffiec-call-report-button"
                      component="div"
                      style={
                        appState.ui.limitInactiveDate ? { marginLeft: 24 } : {}
                      }
                      className={clsx(
                        classes.leaveFDICDialog,
                        !isActive && classes.inactiveButtons,
                      )}
                    >
                      <LeavingFDICDialog
                        webAddress={`https://cdr.ffiec.gov/Public/ViewFacsimileDirect.aspx?ds=call&idType=fdiccert&id=${cert}&date=${risdate}`}
                        label={"Latest FFIEC Call Report"}
                        noHover
                      />
                    </Typography>
                  )}
                  {appState.ui.noFRdata ? (
                    <Button
                      disabled
                      className={clsx(classes.linkButtons, classes.FRNoData)}
                    >
                      Latest UBPR Report
                    </Button>
                  ) : (
                    <Typography
                      id="latest-ubpr-report-button"
                      component="div"
                      style={
                        appState.ui.limitInactiveDate ? { marginLeft: 24 } : {}
                      }
                      className={clsx(
                        classes.leaveFDICDialog,
                        !isActive && classes.inactiveButtons,
                      )}
                    >
                      <LeavingFDICDialog
                        webAddress={`https://cdr.ffiec.gov/Public/ViewFacsimileDirect.aspx?ds=ubpr&repType=1&idType=fdiccert&id=${cert}`}
                        label={"Latest UBPR Report"}
                        noHover
                      />
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
            <FinancialReportDetailsSearch
              data={financialDetailsData}
              fromLevelOne={
                location && location.state ? location.state.fromLevelOne : false
              }
            />
          </>
        </AppBody>
      </Typography>
    </Root>
  );
}
